import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { experienceApi, type EarningsStage } from "@/lib/api/earnings";
import { payrollApi } from "@/lib/api/payroll";
import { ApiRequestError } from "@/lib/api/client";
import { qk } from "@/lib/query-client";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { ErrorState } from "@/components/ui/EmptyState";
import { Icon } from "@/components/layout/Icon";
import { Button } from "@/components/ui/Button";
import { formatDate } from "@/lib/utils/format";
import { useAmountPrivacy } from "./useAmountPrivacy";

const STAGE_LABEL: Record<EarningsStage, string> = {
  pending_attendance: "Attendance pending",
  attendance_confirmed: "Attendance confirmed",
  estimate_updated: "Estimated",
  payroll_processing: "Payroll processing",
  payroll_approved: "Payroll approved",
  payroll_paid: "Paid",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function countdown(days: number) {
  if (days <= 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
}

/** The next payday, what it pays and where that month's payroll stands, with
 * shortcuts to payslips and the finalized pay history. Until payroll for the
 * month is approved the amount is an estimate and says so. */
export function PaydayCard() {
  const navigate = useNavigate();
  const { money } = useAmountPrivacy();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: qk.earnings.payday, queryFn: experienceApi.payday, gcTime: 5 * 60 * 1000 });
  const history = useQuery({ queryKey: qk.payroll.myPayslips, queryFn: payrollApi.myPayslips, gcTime: 5 * 60 * 1000 });

  if (isLoading) {
    return (
      <Card aria-busy="true" data-testid="payday-loading">
        <CardBody className="space-y-3">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-4 w-full" />
        </CardBody>
      </Card>
    );
  }
  if (error) {
    if (error instanceof ApiRequestError && (error.status === 403 || error.status === 404)) return null;
    return (
      <Card>
        <ErrorState title="Couldn't load your payday" onRetry={() => refetch()} className="py-8" />
      </Card>
    );
  }
  if (!data) return null;

  const payslips = (history.data ?? []).slice(0, 6);

  return (
    <Card data-testid="payday-card" className="dark:border-slate-700 dark:bg-slate-900">
      <CardHeader>
        <CardTitle className="dark:text-slate-100">Payday</CardTitle>
        {data.stage && (
          <span className="rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-800 dark:bg-primary-950/60 dark:text-primary-200" data-testid="payday-stage">
            {STAGE_LABEL[data.stage]}
          </span>
        )}
      </CardHeader>
      <CardBody className="space-y-4">
        {!data.configured ? (
          <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="payday-unset">
            Your company has not set a payday yet. Your payslips are listed below once payroll is run.
          </p>
        ) : (
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Next payday</p>
              <p className="mt-0.5 text-2xl font-semibold tracking-tight text-slate-900 dark:text-white" data-testid="payday-date">
                {formatDate(data.date)}
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {countdown(data.days_until)} · pays {data.period_label}
              </p>
            </div>
            {(data.net_pay ?? 0) > 0 ? (
              <div className="text-right" data-testid="payday-net">
                <p className="text-xs text-slate-500 dark:text-slate-400">Net pay (approved)</p>
                <p className="text-xl font-semibold tabular-nums text-slate-900 dark:text-white">{money(data.net_pay)}</p>
              </div>
            ) : (data.forecast ?? 0) > 0 ? (
              <div className="text-right" data-testid="payday-forecast">
                <p className="text-xs text-amber-700 dark:text-amber-400">Estimate, not final</p>
                <p className="text-xl font-semibold tabular-nums text-slate-900 dark:text-white">{money(data.forecast)}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">gross, before deductions</p>
              </div>
            ) : null}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate("/my-payslips")}>
            <Icon name="file-text" className="h-4 w-4" />
            {data.payslip_available ? "Open payslip" : "My payslips"}
          </Button>
        </div>

        <div>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Earnings history</p>
          {history.isLoading ? (
            <Skeleton className="mt-2 h-16 w-full" />
          ) : payslips.length === 0 ? (
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400" data-testid="history-empty">
              Finalized payslips will appear here.
            </p>
          ) : (
            <ul className="mt-2 divide-y divide-slate-100 dark:divide-slate-800" data-testid="payday-history">
              {payslips.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => navigate("/my-payslips")}
                    className="flex w-full items-center justify-between py-2 text-left text-sm hover:text-primary-700 dark:hover:text-primary-300"
                  >
                    <span className="text-slate-700 dark:text-slate-200">
                      {MONTHS[p.period_month - 1]} {p.period_year}
                    </span>
                    <span className="font-medium tabular-nums text-slate-900 dark:text-slate-100">{money(p.net_pay)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
